import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Media from 'react-media';
import uniqBy from 'lodash/uniqBy';
import countBy from 'lodash/countBy';
import isEmpty from 'lodash/isEmpty';
import {
  Button,
  Icon,
  ThemeContext,
  Twemoji,
  RtcLocalPlayer,
  RtcRemotePlayer,
  Avatar,
} from '../../common';
import GiftLine from '../GiftLine';

export default class TeamPlayer extends Component {
  static propTypes = {
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string,
    gifts: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.string.isRequired,
    activePlayerId: PropTypes.string,
    playerId: PropTypes.string,
    giftsAlign: PropTypes.string.isRequired,
    onVoteForKickClicked: PropTypes.func.isRequired,
    onSendGiftClicked: PropTypes.func.isRequired,
    onSelectPlayer: PropTypes.func.isRequired,
    clearNotes: PropTypes.func.isRequired,
    isReady: PropTypes.bool.isRequired,
    isOnline: PropTypes.bool,
    streamId: PropTypes.number,
    videoService: PropTypes.shape({}).isRequired,
    subscribedStreams: PropTypes.shape({}).isRequired,
    user: PropTypes.shape({}).isRequired,
    image: PropTypes.string,
    notes: PropTypes.shape({
      type: PropTypes.string,
    }),
    points: PropTypes.number,
    hasGifts: PropTypes.bool.isRequired,
    isAudiencePlayer: PropTypes.bool.isRequired,
    isVideoRemotelyMuted: PropTypes.bool,
    isGameFinished: PropTypes.bool,
    isInterstitialPlaying: PropTypes.bool.isRequired,
    isVideoPlaying: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    gifts: [],
    isOnline: false,
    streamId: null,
    activePlayerId: null,
    playerId: null,
    image: null,
    points: null,
    notes: {},
    lastName: '',
    isVideoRemotelyMuted: false,
    isGameFinished: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      isStreamPreparing: false,
    };
  }

  createAndPublishLocalStream = async (streamOptions) => {
    const {videoService} = this.props;
    await videoService.createLocalStream(streamOptions);
    await videoService.publishLocalStream();
  }

  onVoteForKickClicked = () => {
    const {id, onVoteForKickClicked} = this.props;
    onVoteForKickClicked(id);
  }

  onSendGiftClicked = () => {
    const {id, onSendGiftClicked} = this.props;
    onSendGiftClicked(id);
  }

  onSelectPlayer = () => {
    const {id, onSelectPlayer} = this.props;
    console.log(id);
    onSelectPlayer(id);
  }

  closeNotes = () => {
    const {id, clearNotes} = this.props;
    clearNotes(id);
  }

  renderPlayerAvatar = (playerStyles) => {
    const {image, firstName, lastName} = this.props;
    return (
      <div className="team-player__avatar" style={playerStyles}>
        <Avatar image={image} fullName={`${firstName} ${lastName}`} iconSize="72" imageWidth={180}/>
      </div>
    );
  }

  renderRemotePlayer = (playerStyles) => {
    const {
      subscribedStreams,
      streamId,
      isInterstitialPlaying,
      isVideoPlaying,
      isVideoRemotelyMuted,
      videoService,
    } = this.props;
    const stream = streamId && subscribedStreams && subscribedStreams[streamId];
    return (
      <RtcRemotePlayer
        stream={stream}
        videoService={videoService}
        isVideoRemotelyMuted={isVideoRemotelyMuted}
        isVideoPlaying={isInterstitialPlaying || isVideoPlaying}
      >
        {this.renderPlayerAvatar(playerStyles)}
      </RtcRemotePlayer>
    );
  };

  renderLocalPlayer = (playerStyles) => {
    const {
      isInterstitialPlaying,
      streamId,
      user: {audioSource, videoSource},
      isVideoPlaying,
      videoService,
    } = this.props;
    const {isStreamPreparing} = this.state;
    if (streamId) {
      const localStreamOptions = {
        streamID: streamId,
        audio: Boolean(audioSource),
        video: Boolean(videoSource),
        cameraId: videoSource,
        microphoneId: audioSource,
        mirror: true,
        screen: false,
      };
      if (videoService.getLocalStream().isClosed() && !isStreamPreparing) {
        this.setState({isStreamPreparing: true});
        this.createAndPublishLocalStream(localStreamOptions).then(() => {
          this.setState({isStreamPreparing: false});
        });
      }
    } else if (!videoService.getLocalStream().isClosed() && !isStreamPreparing) {
      this.setState({isStreamPreparing: true});
      videoService.unPublishLocalStream().then(() => {
        videoService.getLocalStream().close();
        this.setState({isStreamPreparing: false});
      });
    }

    return (
      <RtcLocalPlayer
        stream={videoService.getLocalStream()}
        isInterstitialPlaying={isInterstitialPlaying}
        isVideoPlaying={isVideoPlaying}
      >
        {this.renderPlayerAvatar(playerStyles)}
      </RtcLocalPlayer>
    );
  };

  renderGift = gift => (<GiftLine key={gift.id || gift.name} {...gift}/>)

  renderGifts() {
    const {gifts, giftsAlign} = this.props;
    const counts = countBy(gifts, 'name');
    const sortedGifts = uniqBy(gifts, 'name').map((gift) => {
      gift.count = counts[`${gift.name}`];
      return gift;
    });

    const classes = classNames(
      'team-player__gifts',
      {
        [`team-player__gifts_${giftsAlign}`]: Boolean(giftsAlign),
      }
    );
    return (
      <div className={classes}>{sortedGifts.map(gift => this.renderGift(gift))}</div>
    );
  }

  renderKickNotes = () => {
    const {firstName} = this.props;
    return (
      <div>
        <Icon glyph="boot-s"/>
        <span className="kick-note">{firstName} has been kicked!</span>
      </div>
    );
  }

  renderGiftNotes = () => {
    const {notes: {emoji, senderName}} = this.props;
    const isMobile = (window.innerWidth < 700);

    return (
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isMobile) {
            this.closeNotes();
          }
        }}
      >
        <Twemoji emoji={emoji}/>
        <div className="notification__text"><div className="competitor-name">{senderName}</div>sent you a gift!</div>
        <Button theme="primary" text="ok" size="xxxs" className="button_rounded" handler={this.closeNotes}/>
      </div>
    );
  }

  render() {
    const {
      firstName,
      gifts,
      id,
      isReady,
      isOnline,
      activePlayerId,
      playerId,
      user: {profile: {id: userId}},
      notes,
      points = 0,
      isAudiencePlayer,
      isGameFinished,
    } = this.props;
    const isLocalPlayer = id === userId;
    const playerClasses = classNames(
      'team-player',
      {
        'team-player_gifts': Boolean(gifts.length),
        'team-player_ready': isReady,
        'team-player_wait': !isReady,
        'team-player_offline': !isOnline && !isLocalPlayer,
        'team-player_active': activePlayerId && id === activePlayerId,
        'team-local-player': isLocalPlayer,
        'team-player_selected': Boolean(id === playerId),
      }
    );
    const shouldKickNotesDisplayed = !isEmpty(notes) && notes.type === 'kick' && !isLocalPlayer;
    const shouldEmojiNotesDisplayed = !isEmpty(notes) && notes.type === 'emoji' && isLocalPlayer;
    const playerNotesClasses = classNames(
      'team-player__notes',
      {
        'team-player__notes_visible': shouldKickNotesDisplayed || shouldEmojiNotesDisplayed,
        'team-player__emoji-notes': shouldEmojiNotesDisplayed,
      }
    );

    return (
      <ThemeContext.Consumer>
        {(theme) => {
          const {primaryColor} = theme;
          const styles = {color: primaryColor};
          const playerStyles = isReady ? {borderColor: primaryColor} : null;
          const pointsPlural = points > 1 ? 's' : '';
          const playerPoints = `${points} pt${pointsPlural}`;
          const playerLabel = `${firstName} (${playerPoints})`;

          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li className={playerClasses} style={styles} onClick={this.onSelectPlayer}>
              {isLocalPlayer ?
                this.renderLocalPlayer(playerStyles) :
                this.renderRemotePlayer(playerStyles)
              }
              <span className="team-player__name">{playerLabel}</span>

              <Media query={{minWidth: 700}}>
                {
                  !isLocalPlayer && (
                    <Fragment>
                      <div className="team-player__action">
                        {!isAudiencePlayer && !isGameFinished && <Button theme="primary" text="vote for kick" size="xxs" className="button_rounded" handler={this.onVoteForKickClicked}/>}
                      </div>
                      <button
                        type="button"
                        title="Send a gift"
                        onClick={this.onSendGiftClicked}
                        className="icon-gift team-player__send-gift"
                        style={{backgroundColor: primaryColor}}
                      />
                    </Fragment>
                  )
                }
              </Media>
              <div className="team-player__pointer" style={{backgroundColor: primaryColor}}>
                <Icon glyph="hand-pointer"/>
              </div>
              <div className={playerNotesClasses}>
                {shouldKickNotesDisplayed && this.renderKickNotes()}
                {shouldEmojiNotesDisplayed && this.renderGiftNotes()}
              </div>
              {Boolean(gifts.length) && this.renderGifts()}
            </li>
          );
        }}
      </ThemeContext.Consumer>
    );
  }
}
